<template>
  <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
    <ComponentHeader title="Users">
      <div class="flex gap-2">
        <ExportMenu :disabled="!rowData.length" :exports="[{ type: 'excel', action: exportToExcel }]" />
        <button
          v-if="canCreateUser"
          class="btn-primary-sm"
          @click="showCreateUserForm = true"
        >
          Create User
        </button>
      </div>
    </ComponentHeader>

    <div class="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-gray-100">
      <FilterTabs
        v-model="statusFilter"
        :options="statusFilterOptions"
      />
      <div class="flex flex-wrap items-center gap-3">
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search by name or email..."
          class="w-full sm:w-64 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
        >
        <div class="w-full sm:w-56">
          <SimpleMultiSelect
            v-model="roleFilter"
            :options="roleFilterOptions"
            placeholder="Filter by role"
            searchable
          />
        </div>
      </div>
    </div>

    <div class="overflow-x-auto">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th
              class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none"
              :aria-sort="sortAria('last_name')"
              @click="toggleSort('last_name')"
            >
              Last Name
              <font-awesome-icon
                v-if="sortColumn === 'last_name'"
                :icon="sortDirection === 'asc' ? faSortUp : faSortDown"
                class="ml-1"
              />
            </th>
            <th
              class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none"
              :aria-sort="sortAria('first_name')"
              @click="toggleSort('first_name')"
            >
              First Name
              <font-awesome-icon
                v-if="sortColumn === 'first_name'"
                :icon="sortDirection === 'asc' ? faSortUp : faSortDown"
                class="ml-1"
              />
            </th>
            <th
              class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none"
              :aria-sort="sortAria('email')"
              @click="toggleSort('email')"
            >
              Email
              <font-awesome-icon
                v-if="sortColumn === 'email'"
                :icon="sortDirection === 'asc' ? faSortUp : faSortDown"
                class="ml-1"
              />
            </th>
            <th
              class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none"
              :aria-sort="sortAria('access_roles')"
              @click="toggleSort('access_roles')"
            >
              Roles
              <font-awesome-icon
                v-if="sortColumn === 'access_roles'"
                :icon="sortDirection === 'asc' ? faSortUp : faSortDown"
                class="ml-1"
              />
            </th>
            <th
              class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none"
              :aria-sort="sortAria('portal_roles')"
              @click="toggleSort('portal_roles')"
            >
              Portal Roles
              <font-awesome-icon
                v-if="sortColumn === 'portal_roles'"
                :icon="sortDirection === 'asc' ? faSortUp : faSortDown"
                class="ml-1"
              />
            </th>
            <th
              class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none"
              :aria-sort="sortAria('entity_access_count')"
              @click="toggleSort('entity_access_count')"
            >
              Entities
              <font-awesome-icon
                v-if="sortColumn === 'entity_access_count'"
                :icon="sortDirection === 'asc' ? faSortUp : faSortDown"
                class="ml-1"
              />
            </th>
            <th
              class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none"
              :aria-sort="sortAria('is_active')"
              @click="toggleSort('is_active')"
            >
              Status
              <font-awesome-icon
                v-if="sortColumn === 'is_active'"
                :icon="sortDirection === 'asc' ? faSortUp : faSortDown"
                class="ml-1"
              />
            </th>
            <th
              v-if="canViewProviderMessaging"
              class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Provider Messaging Inbox
            </th>
            <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <template v-if="loading">
            <tr v-for="row in 6" :key="`skeleton-${row}`">
              <td class="px-4 py-3"><div class="h-4 w-28 bg-gray-200 rounded animate-pulse"></div></td>
              <td class="px-4 py-3"><div class="h-4 w-24 bg-gray-200 rounded animate-pulse"></div></td>
              <td class="px-4 py-3"><div class="h-4 w-52 bg-gray-200 rounded animate-pulse"></div></td>
              <td class="px-4 py-3"><div class="h-5 w-20 bg-gray-200 rounded-full animate-pulse"></div></td>
              <td class="px-4 py-3"><div class="h-5 w-24 bg-gray-200 rounded-full animate-pulse"></div></td>
              <td class="px-4 py-3"><div class="h-4 w-8 bg-gray-200 rounded animate-pulse mx-auto"></div></td>
              <td class="px-4 py-3"><div class="h-4 w-16 bg-gray-200 rounded animate-pulse"></div></td>
              <td v-if="canViewProviderMessaging" class="px-4 py-3"><div class="h-4 w-4 bg-gray-200 rounded animate-pulse mx-auto"></div></td>
              <td class="px-4 py-3"><div class="w-5 h-5 bg-gray-200 rounded-full animate-pulse ml-auto"></div></td>
            </tr>
          </template>
          <tr v-else-if="!filteredAndSortedUsers.length">
            <td :colspan="canViewProviderMessaging ? 9 : 8" class="px-4 py-8 text-center text-sm text-gray-500">
              No users match the current search or filters.
            </td>
          </tr>
          <tr
            v-for="user in filteredAndSortedUsers"
            :key="user.id"
            class="cursor-pointer hover:bg-gray-50"
            @click="$router.push('/admin/user/' + user.id)"
          >
            <td class="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
              <router-link
                :to="`/admin/user/${user.id}`"
                class="text-primary-600 hover:text-primary-800 hover:underline"
                @click.stop
              >
                {{ user.last_name }}
              </router-link>
            </td>
            <td class="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
              <router-link
                :to="`/admin/user/${user.id}`"
                class="text-primary-600 hover:text-primary-800 hover:underline"
                @click.stop
              >
                {{ user.first_name }}
              </router-link>
            </td>
            <td class="px-4 py-2 text-sm text-gray-900">{{ user.email }}</td>
            <td class="px-4 py-2 text-sm text-gray-900">
              <div class="flex flex-wrap gap-1">
                <Tag
                  v-for="role in user.pretty_access_roles"
                  :key="role"
                  :label="role"
                  color="primary"
                />
              </div>
            </td>
            <td class="px-4 py-2 text-sm text-gray-900">
              <div class="flex flex-wrap gap-1">
                <Tag
                  v-for="role in user.portal_roles || []"
                  :key="role.role_name"
                  :label="role.role_name"
                  color="secondary"
                />
              </div>
            </td>
            <td class="px-4 py-2 text-sm text-gray-900 text-center">
              {{ user.entity_access_count }}
            </td>
            <td class="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
              <span v-if="user.is_active" class="inline-flex items-center gap-1 text-green-700">
                <font-awesome-icon :icon="faCheck" />
                Active
              </span>
              <span v-else class="text-gray-500">Inactive</span>
            </td>
            <td v-if="canViewProviderMessaging" class="px-4 py-2 text-center">
              <span
                v-if="user.provider_mailbox_status === 'active'"
                :aria-label="`${user.full_name || user.email} has an active Provider Messaging inbox`"
                role="img"
              >
                <font-awesome-icon :icon="faCheck" class="text-green-700" />
              </span>
            </td>
            <td class="px-4 py-2 text-right" @click.stop>
              <DropdownMenu>
                <DropdownMenuItem
                  v-for="item in getMenuItems(user)"
                  :key="item.label"
                  @click="item.action()"
                >
                  {{ item.label }}
                </DropdownMenuItem>
              </DropdownMenu>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="!loading" class="px-4 py-3 text-sm text-gray-500 border-t border-gray-100">
      Showing {{ filteredAndSortedUsers.length }} of {{ processedRowData.length }} users
    </div>

    <Create_User_Form
      v-if="showCreateUserForm"
      @close="showCreateUserForm = false"
      @user-created="handleUserCreated"
    />
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCheck, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { mapGetters } from 'vuex';
import { listUsers, updateUser } from '@/services/api/adminUsers';
import { FEATURES } from '@/constants/features';
import { canAccessFeature } from '@/utils/access';
import ComponentHeader from '@/components/widgetcomponents/library/ComponentHeader.vue';
import Create_User_Form from '@/components/forms/Create_User_Form.vue';
import DropdownMenu from '@/components/widgetcomponents/ui/DropdownMenu.vue';
import DropdownMenuItem from '@/components/widgetcomponents/ui/DropdownMenuItem.vue';
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue';
import FilterTabs from '@/components/widgetcomponents/library/FilterTabs.vue';
import SimpleMultiSelect from '@/components/widgetcomponents/Dropdown_Selector/SimpleMultiSelect.vue';
import Tag from '@/components/widgetcomponents/library/Tag.vue';
import { exportToExcel } from '@/utils/exportArraytoXlsx.js';

export default {
  components: {
    ComponentHeader,
    Create_User_Form,
    DropdownMenu,
    DropdownMenuItem,
    ExportMenu,
    FilterTabs,
    FontAwesomeIcon,
    SimpleMultiSelect,
    Tag,
  },
  data() {
    return {
      faCheck,
      faSortDown,
      faSortUp,
      loading: true,
      roleFilter: [],
      rowData: [],
      searchQuery: '',
      showCreateUserForm: false,
      sortColumn: 'last_name',
      sortDirection: 'asc',
      statusFilter: 'all',
      statusFilterOptions: [
        { value: 'all', label: 'All' },
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
      ],
    };
  },
  computed: {
    ...mapGetters('auth', ['current_user']),
    ...mapGetters('tenant', ['current_tenant_slug']),
    ...mapGetters('featureConfig', ['roles']),
    canCreateUser() {
      return canAccessFeature(FEATURES.ACTION_USER_CREATE);
    },
    canToggleActive() {
      return canAccessFeature(FEATURES.ACTION_USER_CREATE, { superuserOverride: false });
    },
    canViewProviderMessaging() {
      return canAccessFeature(FEATURES.MODULE_PROVIDER_MESSAGING, { superuserOverride: false });
    },
    configuredRoles() {
      return Array.isArray(this.roles) ? this.roles : [];
    },
    processedRowData() {
      return this.rowData.map(user => ({
        ...user,
        pretty_access_roles: user.access_roles.map(roleId => {
          const found = this.configuredRoles.find(role => role.role_id === roleId);
          return found ? found.display_name : roleId;
        }),
      }));
    },
    roleFilterOptions() {
      return this.configuredRoles.map(role => ({
        value: role.role_id,
        label: role.display_name,
      }));
    },
    filteredAndSortedUsers() {
      let users = this.processedRowData;

      if (this.statusFilter === 'active') {
        users = users.filter(user => user.is_active);
      } else if (this.statusFilter === 'inactive') {
        users = users.filter(user => !user.is_active);
      }

      if (this.roleFilter.length) {
        users = users.filter(user =>
          (user.access_roles || []).some(role => this.roleFilter.includes(role))
        );
      }

      const query = this.searchQuery.trim().toLowerCase();
      if (query) {
        users = users.filter(user =>
          (user.first_name || '').toLowerCase().includes(query) ||
          (user.last_name || '').toLowerCase().includes(query) ||
          (user.email || '').toLowerCase().includes(query)
        );
      }

      const direction = this.sortDirection === 'asc' ? 1 : -1;
      return [...users].sort((a, b) => {
        const aValue = this.sortValue(a, this.sortColumn);
        const bValue = this.sortValue(b, this.sortColumn);
        return aValue < bValue ? -direction : aValue > bValue ? direction : 0;
      });
    },
  },
  watch: {
    current_tenant_slug() {
      this.$store.dispatch('featureConfig/fetchConfig');
      this.fetchUsers();
    },
  },
  mounted() {
    // featureConfig.roles is needed to resolve role_id → display_name below.
    this.$store.dispatch('featureConfig/fetchConfig');
    this.fetchUsers();
  },
  methods: {
    async fetchUsers() {
      this.loading = true;
      try {
        this.rowData = await listUsers();
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        this.loading = false;
      }
    },
    getMenuItems(user) {
      const menuItems = [
        {
          label: 'Edit',
          action: () => this.$router.push(`/admin/user/${user.id}`),
        },
      ];

      if (this.canToggleActive && user.id !== this.current_user?.id) {
        menuItems.push({
          label: user.is_active ? 'Block Access' : 'Permit Access',
          action: () => this.toggleUserActive(user),
        });
      }

      return menuItems;
    },
    sortAria(column) {
      if (this.sortColumn !== column) return 'none';
      return this.sortDirection === 'asc' ? 'ascending' : 'descending';
    },
    sortValue(user, column) {
      switch (column) {
        case 'last_name':
          return `${user.last_name || ''} ${user.first_name || ''}`.toLowerCase();
        case 'first_name':
          return (user.first_name || '').toLowerCase();
        case 'access_roles':
          return (user.pretty_access_roles || []).join(', ').toLowerCase();
        case 'portal_roles':
          return (user.portal_roles || []).map(role => role.role_name).join(', ').toLowerCase();
        case 'entity_access_count':
          return user.entity_access_count;
        case 'email':
          return (user.email || '').toLowerCase();
        default:
          return user[column] ?? '';
      }
    },
    toggleSort(column) {
      if (this.sortColumn === column) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortColumn = column;
        this.sortDirection = 'asc';
      }
    },
    exportToExcel() {
      const rows = this.processedRowData.map((user) => ({
        'Last Name': user.last_name || '',
        'First Name': user.first_name || '',
        'Email': user.email || '',
        'Roles': user.pretty_access_roles.join(', '),
        'Portal Task Types': (user.portal_roles || []).map((role) => role.role_name).join(', '),
        '# Entities': user.entity_access_count,
        'Enabled': user.is_active ? 'Yes' : 'No',
      }));

      const today = new Date().toISOString().split('T')[0];
      exportToExcel(rows, `users_${today}.xlsx`, {
        sheetName: 'Users',
        columnWidths: {
          'Last Name': 140,
          'First Name': 120,
          'Email': 250,
          'Roles': 200,
          'Portal Task Types': 180,
          '# Entities': 90,
          'Enabled': 80,
        },
      });
    },
    async handleUserCreated() {
      await this.fetchUsers();
    },
    async toggleUserActive(user) {
      if (this.loading) return;
      this.loading = true;
      const newIsActive = !user.is_active;
      try {
        await updateUser(user.id, { is_active: newIsActive });
        const index = this.rowData.findIndex(existingUser => existingUser.id === user.id);
        if (index !== -1) {
          this.rowData[index] = { ...this.rowData[index], is_active: newIsActive };
        }
      } catch (error) {
        console.error('Failed to update user status.', error);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
